import React, { useState, useEffect, useRef } from 'react';
import { notify, confirmDialog } from './ui/notify';
import { apiCall, getApiBaseUrl, authHeaders, withToken } from './config';
import WhatsAppChat from './WhatsAppChat';
import { fmtBirthDE, DocView } from './DigitalWorkContract';

// Kompakte PerformanceRating für Status-Integration
const CompactPerformanceRating = ({ user, onUpdate }) => {
  const [performance, setPerformance] = useState(user.performance_rating || 'normal');
  const [loading, setLoading] = useState(false);

  // Update performance when user prop changes
  useEffect(() => {
    setPerformance(user.performance_rating || 'normal');
  }, [user.id, user.performance_rating]);

  const handlePerformanceChange = async (value) => {
    setLoading(true);
    
    try {
      await onUpdate(user.id, 'performance_rating', value);
      setPerformance(value);
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Mitarbeiterbewertung:', error);
      notify('Fehler beim Speichern. Bitte versuchen Sie es erneut.');
      // Zurücksetzen bei Fehler
      setPerformance(user.performance_rating || 'normal');
    } finally {
      setLoading(false);
    }
  };

  const getPerformanceIcon = (value) => {
    switch(value) {
      case 'gut': return '🟢';
      case 'normal': return '🟡';
      case 'schlecht': return '🔴';
      default: return '⚪';
    }
  };

  const getDisplayText = (value) => {
    switch(value) {
      case 'gut': return 'Gut';
      case 'normal': return 'Normal';
      case 'schlecht': return 'Schlecht';
      default: return 'Unbekannt';
    }
  };

  // Kompakt: nur die Ampel – Klick schaltet weiter (gut → normal → schlecht → gut)
  const cycle = () => {
    const order = ['gut', 'normal', 'schlecht'];
    const next = order[(order.indexOf(performance) + 1) % order.length];
    handlePerformanceChange(next);
  };

  return (
    <button
      type="button"
      onClick={cycle}
      disabled={loading}
      className="btn btn-sm p-0 border-0 bg-transparent"
      style={{ fontSize: '18px', lineHeight: 1, opacity: loading ? 0.5 : 1 }}
      title={`Bewertung: ${getDisplayText(performance)} – klicken zum Weiterschalten`}
    >
      {getPerformanceIcon(performance)}{loading ? '⏳' : ''}
    </button>
  );
};



// AccountantTrafficLight Komponente
const AccountantTrafficLight = ({ user, onUpdate }) => {
  const [status, setStatus] = useState({
    employee_created: user.accountant_employee_created || false,
    employee_billed: user.accountant_employee_billed || false
  });
  
  const [loading, setLoading] = useState({
    employee_created: false,
    employee_billed: false
  });

  // Update status when user prop changes
  useEffect(() => {
    setStatus({
      employee_created: user.accountant_employee_created || false,
      employee_billed: user.accountant_employee_billed || false
    });
  }, [user.id, user.accountant_employee_created, user.accountant_employee_billed]);

  const handleStatusChange = async (field, value) => {
    setLoading(prev => ({ ...prev, [field]: true }));
    
    try {
      await onUpdate(user.id, field, value);
      // Nur bei erfolgreichem Backend-Call aktualisieren
      setStatus(prev => ({ ...prev, [field]: value }));
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Buchhalter-Status:', error);
      // Hier könnte man eine Fehlermeldung anzeigen
      notify('Fehler beim Speichern. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(prev => ({ ...prev, [field]: false }));
    }
  };

  const getTrafficLightColor = () => {
    if (status.employee_created && status.employee_billed) {
      return '🟢'; // Grün - alles erledigt
    } else if (status.employee_created) {
      return '🟡'; // Gelb - angelegt, aber noch nicht abgerechnet
    } else {
      return '🔴'; // Rot - noch nichts erledigt
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ fontSize: '16px' }}>{getTrafficLightColor()}</span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <label style={{ fontSize: '11px', margin: 0, opacity: loading.employee_created ? 0.6 : 1 }}>
          <input
            type="checkbox"
            checked={status.employee_created}
            disabled={loading.employee_created}
            onChange={(e) => handleStatusChange('employee_created', e.target.checked)}
            style={{ marginRight: '4px' }}
          />
          Angelegt {loading.employee_created && '⏳'}
        </label>
        <label style={{ fontSize: '11px', margin: 0, opacity: loading.employee_billed ? 0.6 : 1 }}>
          <input
            type="checkbox"
            checked={status.employee_billed}
            disabled={loading.employee_billed}
            onChange={(e) => handleStatusChange('employee_billed', e.target.checked)}
            style={{ marginRight: '4px' }}
          />
          Abgerechnet {loading.employee_billed && '⏳'}
        </label>
      </div>
    </div>
  );
};

// Geburtsdatum-Konvertierungsfunktionen
const formatBirthdateForInput = (dbBirthdate) => {
  if (!dbBirthdate || dbBirthdate.length !== 8) return '';
  
  // Von ttmmyyyy zu YYYY-MM-DD konvertieren
  const day = dbBirthdate.substring(0, 2);
  const month = dbBirthdate.substring(2, 4);
  const year = dbBirthdate.substring(4, 8);
  
  return `${year}-${month}-${day}`;
};

const formatBirthdateForDisplay = (dbBirthdate) => {
  if (!dbBirthdate || dbBirthdate.length !== 8) return dbBirthdate || '';
  
  // Von ttmmyyyy zu tt.mm.yyyy konvertieren
  const day = dbBirthdate.substring(0, 2);
  const month = dbBirthdate.substring(2, 4);
  const year = dbBirthdate.substring(4, 8);
  
  return `${day}.${month}.${year}`;
};

const formatBirthdateForDatabase = (inputBirthdate) => {
  if (!inputBirthdate) return '';
  
  // Von YYYY-MM-DD zu ttmmyyyy konvertieren
  const parts = inputBirthdate.split('-');
  if (parts.length !== 3) return inputBirthdate;
  
  const year = parts[0];
  const month = parts[1];
  const day = parts[2];
  
  return `${day}${month}${year}`;
};

// Status-System: MANUELLE Tags (mehrfach kombinierbar, klickbare Chips) plus
// AUTOMATISCH abgeleitete Badges (Vertrag, Verfügbarkeit, geplant, Dienstplan
// bestätigt, WhatsApp) aus /api/users/status-overview – nichts mehr von Hand pflegen.
const MANUAL_TAGS = [
  { key: 'kontaktiert', icon: '💬', label: 'Kontaktiert', btn: 'outline-primary', badge: 'primary' },
  { key: 'zugesagt', icon: '👍', label: 'Zugesagt', btn: 'outline-success', badge: 'success' },
  { key: 'abgesagt', icon: '❌', label: 'Abgesagt', btn: 'outline-danger', badge: 'danger' },
];

const parseTags = (u) => {
  try {
    const t = JSON.parse(u.status_tags || '[]');
    return Array.isArray(t) ? t : [];
  } catch (_) { return []; }
};

function UserManagement({ user }) {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  // Buchhalter-Filter: 'all' | 'yes' | 'no' – je für „angelegt" und „abgerechnet"
  const [accFilter, setAccFilter] = useState({ created: 'all', billed: 'all' });
  // Dokument im App-Vollbild (mit Klick-Zoom) statt im neuen Tab: { url, mime, alt }
  const [docPreview, setDocPreview] = useState(null);
  // Klick-Zoom im Vertragsdokument-Modal (Ausweis aus der Dokumente-Spalte): null | { x, y }
  const [cdZoom, setCdZoom] = useState(null);
  const cdScrollRef = useRef(null);
  useEffect(() => {
    const box = cdScrollRef.current;
    if (!cdZoom || !box) return;
    box.scrollLeft = cdZoom.x * box.scrollWidth - box.clientWidth / 2;
    box.scrollTop = cdZoom.y * box.scrollHeight - box.clientHeight / 2;
  }, [cdZoom]);
  const [showModal, setShowModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showWorkYearModal, setShowWorkYearModal] = useState(false);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDocuments, setUserDocuments] = useState([]);
  const [userWorkYears, setUserWorkYears] = useState([]);
  const [partnerPreferences, setPartnerPreferences] = useState([]);
  const [partnerData, setPartnerData] = useState({
    preferred_partner_id: '',
    priority: 1,
    notes: '',
    bidirectional: true
  });
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  const [statusOverview, setStatusOverview] = useState({});
  const [contractView, setContractView] = useState(null); // Vertragsdaten-Modal (Buchhalter-Sicht)
  const [copiedKey, setCopiedKey] = useState(null); // Feedback fürs Kopieren im Vertragsdaten-Modal
  // Höhe der oberen (sticky) Navbar – die Spaltenleiste muss DARUNTER andocken,
  // sonst verschwindet sie hinter der Navigation.
  const [navH, setNavH] = useState(56);
  useEffect(() => {
    const nav = document.querySelector('nav.navbar');
    if (nav) setNavH(nav.offsetHeight);
  }, []);
  const [showInactive, setShowInactive] = useState(false);
  const [hideRejected, setHideRejected] = useState(true);
  const [hideNochKeineRueckmeldung, setHideNochKeineRueckmeldung] = useState(true);
  const [documentFile, setDocumentFile] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showWhatsAppChat, setShowWhatsAppChat] = useState(false);
  const [selectedChatUser, setSelectedChatUser] = useState(null);
  const [documents, setDocuments] = useState({});
  const [contractDocs, setContractDocs] = useState({}); // {userId: {idCardImage, workPermitDocument, employeeNationality}}
  const [showContractDocModal, setShowContractDocModal] = useState(false);
  const [contractDocImage, setContractDocImage] = useState(null);
  const [contractDocTitle, setContractDocTitle] = useState('');
  
  // Sortierung und Suche
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    birthdate: '',
    password: '',
    role: 'mitarbeiter',
    phone: '',
    email: '',
    work_permit_valid: 'not_required',
    current_year_active: true,
    work_years: new Date().getFullYear().toString(),
    can_work_cash_register: false,
    can_work_wiesngarten: false,
    performance_rating: 'normal'
  });

  const [documentData, setDocumentData] = useState({
    document_type: 'arbeitsvertrag',
    status: 'genehmigt',
    notes: '',
    year: new Date().getFullYear()
  });

  const [workYearData, setWorkYearData] = useState({
    year: new Date().getFullYear(),
    is_active: true,
    notes: ''
  });

  // Filter-Einstellungen beim Laden initialisieren
  useEffect(() => {
    const savedHideRejected = localStorage.getItem('hideRejected');
    const savedHideNochKeineRueckmeldung = localStorage.getItem('hideNochKeineRueckmeldung');
    
    if (savedHideRejected !== null) {
      setHideRejected(JSON.parse(savedHideRejected));
    }
    if (savedHideNochKeineRueckmeldung !== null) {
      setHideNochKeineRueckmeldung(JSON.parse(savedHideNochKeineRueckmeldung));
    }
  }, []);

  // Filter-Einstellungen speichern wenn sie sich ändern
  useEffect(() => {
    localStorage.setItem('hideRejected', JSON.stringify(hideRejected));
  }, [hideRejected]);

  useEffect(() => {
    localStorage.setItem('hideNochKeineRueckmeldung', JSON.stringify(hideNochKeineRueckmeldung));
  }, [hideNochKeineRueckmeldung]);

  useEffect(() => {
    fetchUsers();
    fetchDocuments();
    fetchContractDocs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Dokumente + abgeleitete Status neu laden wenn sich das Jahr ändert
  useEffect(() => {
    fetchDocuments();
    fetchContractDocs();
    fetchStatusOverview();
  }, [yearFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    filterUsers();
  }, [users, yearFilter, showInactive, hideRejected, hideNochKeineRueckmeldung, searchTerm, sortConfig, accFilter, statusOverview]); // eslint-disable-line react-hooks/exhaustive-deps

  // Abgeleitete Status-Infos (Vertrag/Verfügbarkeit/geplant/bestätigt/Buchhalter) fürs Jahr
  const fetchStatusOverview = () => {
    const y = yearFilter || new Date().getFullYear().toString();
    apiCall(`/users/status-overview?year=${y}`).then(setStatusOverview).catch(() => setStatusOverview({}));
  };

  const fetchUsers = async () => {
    try {
      fetchStatusOverview();
      const data = await apiCall('/users');
      
      if (Array.isArray(data)) {
        // Lade für jeden User die Arbeitsjahre
        const usersWithYears = await Promise.all(data.map(async (user) => {
          try {
            const yearsData = await apiCall(`/user-work-years/${user.id}`);
            return { ...user, workYears: yearsData };
          } catch (err) {
            console.error(`Fehler beim Laden der Arbeitsjahre für User ${user.id}:`, err);
            return { ...user, workYears: [] };
          }
        }));
        
        setUsers(usersWithYears);
      } else {
        console.error('API Fehler:', data);
        setUsers([]);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Benutzer:', err);
      setUsers([]);
    }
  };

  const fetchDocuments = async () => {
    try {
      const currentYear = yearFilter || new Date().getFullYear().toString();
      const docs = await apiCall(`/documents?userRole=admin&year=${currentYear}`);
      
      // Gruppiere Dokumente nach User-ID und Typ
      const docsByUser = {};
      docs.forEach(doc => {
        if (!docsByUser[doc.user_id]) {
          docsByUser[doc.user_id] = {};
        }
        docsByUser[doc.user_id][doc.document_type] = doc;
      });
      
      setDocuments(docsByUser);
    } catch (error) {
      console.error('Fehler beim Laden der Dokumente:', error);
    }
  };

  // Nur VORHANDEN-Flags laden (ein schneller Request für alle) – die Bilder selbst
  // werden erst beim Klick aufs Auge nachgeladen. Vorher wurden hier für jeden
  // Mitarbeiter die kompletten Base64-Ausweise geladen; viele Requests scheiterten,
  // wodurch Augen/Dokumente scheinbar zufällig fehlten.
  const fetchContractDocs = async () => {
    try {
      const currentYear = yearFilter || new Date().getFullYear().toString();
      const flags = await apiCall(`/employee-contracts/doc-flags?year=${currentYear}`);
      setContractDocs(flags && typeof flags === 'object' ? flags : {});
    } catch (error) {
      console.error('Fehler beim Laden der Vertragsdokument-Flags:', error);
      setContractDocs({});
    }
  };

  // Dokument aus dem Vertrag erst beim Klick laden und anzeigen (PDF → neuer Tab)
  const viewContractDoc = async (targetUser, key, title) => {
    try {
      const currentYear = yearFilter || new Date().getFullYear().toString();
      const d = await apiCall(`/employee-contracts/documents/${targetUser.id}?year=${currentYear}`);
      const img = d && d[key];
      if (!img) return notify('Dokument nicht gefunden');
      if (String(img).startsWith('data:application/pdf')) {
        // Chrome zeigt data:-PDFs nicht an → als Blob-URL im neuen Tab öffnen
        const b64 = img.split(',')[1];
        const bytes = atob(b64);
        const arr = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
        window.open(URL.createObjectURL(new Blob([arr], { type: 'application/pdf' })), '_blank');
        return;
      }
      setContractDocImage(img);
      setContractDocTitle(title);
      setShowContractDocModal(true);
    } catch (e) {
      notify('Fehler beim Laden des Dokuments');
    }
  };

  // Manuellen Tag an-/abwählen (mehrere gleichzeitig möglich)
  const handleTagToggle = async (targetUser, tag) => {
    const current = parseTags(targetUser);
    const tags = current.includes(tag) ? current.filter((t) => t !== tag) : [...current, tag];
    try {
      await apiCall(`/users/${targetUser.id}/status-tags`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tags }),
      });
      // Lokal aktualisieren (kein teurer Komplett-Reload nötig)
      const legacy = tags.includes('abgesagt') ? 'abgesagt'
        : tags.includes('kontaktiert') ? 'In Kontakt' : 'noch keine Rückmeldung';
      setUsers((prev) => prev.map((u) =>
        u.id === targetUser.id ? { ...u, status_tags: JSON.stringify(tags), status: legacy } : u));
    } catch (error) {
      console.error('Netzwerk-Fehler:', error);
      notify('Fehler beim Aktualisieren des Status');
    }
  };

  const handleBulkTag = async (tag) => {
    const label = tag
      ? `alle ${filteredUsers.length} sichtbaren Mitarbeiter zusätzlich als "${tag}" markieren`
      : `die manuellen Markierungen aller ${filteredUsers.length} sichtbaren Mitarbeiter zurücksetzen`;
    if (!await confirmDialog(`Möchtest du wirklich ${label}?`)) return;
    try {
      await Promise.all(filteredUsers.map((u) => {
        const tags = tag ? Array.from(new Set([...parseTags(u), tag])) : [];
        return apiCall(`/users/${u.id}/status-tags`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tags }),
        });
      }));
      fetchUsers();
      notify(`Status von ${filteredUsers.length} Mitarbeitern aktualisiert.`);
    } catch (error) {
      console.error('Fehler beim Bulk-Update:', error);
      notify('Fehler beim Aktualisieren der Status');
    }
  };

  const openWhatsAppChat = (user) => {
    setSelectedChatUser(user);
    setShowWhatsAppChat(true);
  };

  const closeWhatsAppChat = () => {
    setShowWhatsAppChat(false);
    setSelectedChatUser(null);
  };

  // Sortierung handhaben
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sortierungs-Icon für Spaltenheader
  const getSortIcon = (columnKey) => {
    if (sortConfig.key === columnKey) {
      return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    }
    return ' ↕';
  };

  // Dokument-Handler  
  const handleViewDocument = async (docId, userId = null) => {
    try {
      // Direkte URL-Methode mit Sicherheitsprüfung
      const baseUrl = getApiBaseUrl();
      
      // Benutzer-ID ermitteln: Parameter > selectedUser > ersten Admin
      const currentUserId = userId || selectedUser?.id || users.find(u => u.role === 'admin')?.id;
      
      if (!currentUserId) {
        notify('Fehler: Benutzer-ID nicht verfügbar');
        return;
      }
      
      const documentUrl = `${baseUrl}/documents/${docId}/view?user_id=${currentUserId}`;
      
      console.log('Opening document URL:', documentUrl);
      
      // Erst prüfen ob die URL erreichbar ist (mit Token)
      const response = await fetch(documentUrl, { method: 'HEAD', headers: authHeaders() });
      if (response.ok) {
        // Bilder und PDFs im App-Vollbild zeigen – dort lässt sich per Klick
        // zoomen (Ausweisnummer!). Alles andere weiterhin im neuen Tab.
        try {
          const full = await fetch(documentUrl, { headers: authHeaders() });
          const blob = await full.blob();
          const mime = blob.type || '';
          if (mime.startsWith('image/') || mime === 'application/pdf') {
            const doc = (userDocuments || []).find((d) => d.id === docId);
            setDocPreview({ url: URL.createObjectURL(blob), mime,
              alt: (doc && (doc.document_type || doc.doc_type || doc.filename)) || 'Dokument' });
            return;
          }
        } catch (_) { /* dann eben im neuen Tab */ }
        // URL mit Token öffnen (window.open kann keinen Header senden)
        window.open(withToken(documentUrl), '_blank');
      } else {
        console.error('Document not accessible:', response.status, response.statusText);
        if (response.status === 401) {
          notify('Fehler: Zugriff verweigert - Sie sind nicht berechtigt');
        } else if (response.status === 403) {
          notify('Fehler: Dieses Dokument gehört Ihnen nicht');
        } else {
          notify('Fehler beim Anzeigen des Dokuments: ' + response.status);
        }
      }
    } catch (error) {
      console.error('Fehler beim Anzeigen des Dokuments:', error);
      notify('Fehler beim Anzeigen des Dokuments: ' + error.message);
    }
  };

  const handleDownloadDocument = async (docId, filename, user, docType) => {
    try {
      // Für Dokument-Downloads müssen wir direkten fetch verwenden, da apiCall JSON erwartet
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/documents/${docId}/download`, { headers: authHeaders() });
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        
        // Automatische Namensgebung: Vorname_Nachname_Dokumenttyp.extension
        const fileExtension = filename.split('.').pop();
        const docTypeNames = {
          'arbeitserlaubnis': 'Arbeitserlaubnis',
          'arbeitsvertrag': 'Arbeitsvertrag',
          'aufenthaltserlaubnis': 'Aufenthaltserlaubnis'
        };
        const docTypeName = docTypeNames[docType] || docType;
        
        // Verwende firstname/lastname wenn verfügbar, sonst name als Fallback
        let firstName = user.firstname || '';
        let lastName = user.lastname || '';
        
        if (!firstName && !lastName && user.name) {
          const nameParts = user.name.split(' ');
          firstName = nameParts[0] || '';
          lastName = nameParts.slice(1).join(' ') || '';
        }
        
        const automaticFilename = `${firstName}_${lastName}_${docTypeName}.${fileExtension}`;
        
        a.download = automaticFilename;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        notify('Fehler beim Download des Dokuments');
      }
    } catch (error) {
      console.error('Fehler beim Download des Dokuments:', error);
      notify('Fehler beim Download des Dokuments');
    }
  };

  // Vertragsdaten-Ansicht (kompakt, für den Buchhalter): lädt den Vertrag nach
  // und zeigt die abrechnungsrelevanten Felder ohne das riesige Formular.
  const openContractData = async (targetUser, contractId, statusLabel) => {
    try {
      const full = await apiCall(`/employee-contracts/${contractId}`);
      let d = {};
      try { d = typeof full.contract_data === 'string' ? JSON.parse(full.contract_data) : (full.contract_data || {}); } catch (_) {}
      setContractView({
        userId: targetUser.id,
        user: targetUser,
        name: `${targetUser.firstname} ${targetUser.lastname}`,
        statusLabel,
        rejection: full.rejection_reason || null,
        submitted: full.submitted_at,
        d,
      });
    } catch (e) {
      notify('Fehler beim Laden der Vertragsdaten');
    }
  };

  // Gemeinsame Dokumente-Zelle: 🪪 Ausweis · 📄 Vertrag (erst wenn genehmigt) ·
  // 💼 Arbeitserlaubnis · 🏠 Aufenthaltstitel. ⚠️ wenn Erlaubnis/Titel bei
  // Nicht-EU fehlt; bei EU-Bürgern wird dafür gar nichts angezeigt.
  const renderDocsCell = (currentUser) => {
    const flags = contractDocs[currentUser.id] || {};
    const docTable = documents[currentUser.id] || {};
    const btn = (icon, title, onClick, cls = 'btn-outline-primary') => (
      <button key={title} className={`btn btn-sm ${cls}`} style={{ padding: '2px 6px' }}
        onClick={onClick} title={title}>{icon}</button>
    );
    const warn = (title) => (
      <span key={title} title={title} style={{ fontSize: 16, cursor: 'help' }}>⚠️</span>
    );
    const items = [];
    // 🪪 Ausweis (aus dem Vertrag)
    if (flags.has_id) items.push(btn('🪪', `Ausweis – ${currentUser.firstname} ${currentUser.lastname}`,
      () => viewContractDoc(currentUser, 'idCardImage', `Ausweis - ${currentUser.firstname} ${currentUser.lastname}`)));
    // 📄 Vertragsdaten (für den Buchhalter: SVNR, Steuer-ID, IBAN usw.) – Farbe = Status
    if (flags.contract_id) {
      const cls = flags.contract_status === 'approved' ? 'btn-outline-success'
        : flags.contract_status === 'submitted' ? 'btn-outline-info'
        : flags.contract_status === 'rejected' ? 'btn-outline-danger' : 'btn-outline-warning';
      const label = { approved: 'genehmigt', submitted: 'eingereicht', rejected: 'abgelehnt', draft: 'Entwurf' }[flags.contract_status] || flags.contract_status;
      items.push(btn('📄', `Vertragsdaten anzeigen (${label})`,
        () => openContractData(currentUser, flags.contract_id, label), cls));
    }
    // 📑 Arbeitsvertrag als PDF (wird bei Genehmigung automatisch abgelegt)
    if (docTable.arbeitsvertrag) {
      items.push(btn('📑', 'Arbeitsvertrag (PDF) öffnen',
        () => handleViewDocument(docTable.arbeitsvertrag.id, currentUser.id), 'btn-outline-success'));
    }
    // 💼 Arbeitserlaubnis (Vertrag oder separat hochgeladen), ⚠️ wenn nötig aber fehlt
    if (flags.has_wp) items.push(btn('💼', 'Arbeitserlaubnis anzeigen',
      () => viewContractDoc(currentUser, 'workPermitDocument', `Arbeitserlaubnis - ${currentUser.firstname} ${currentUser.lastname}`)));
    else if (docTable.arbeitserlaubnis) items.push(btn('💼', 'Arbeitserlaubnis anzeigen',
      () => handleViewDocument(docTable.arbeitserlaubnis.id, currentUser.id)));
    else if (flags.needs_permit) items.push(warn('Arbeitserlaubnis FEHLT (Nicht-EU)'));
    // 🏠 Aufenthaltstitel, ⚠️ wenn nötig aber fehlt
    if (flags.has_rp) items.push(btn('🏠', 'Aufenthaltstitel anzeigen',
      () => viewContractDoc(currentUser, 'residencePermitDocument', `Aufenthaltstitel - ${currentUser.firstname} ${currentUser.lastname}`)));
    else if (flags.needs_permit) items.push(warn('Aufenthaltstitel FEHLT (Nicht-EU)'));

    if (!items.length) return <span className="text-muted">-</span>;
    return <div className="d-flex gap-1 align-items-center">{items}</div>;
  };

  const filterUsers = () => {
    let filtered = users;

    // Echtzeitsuche anwenden
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(user => {
        const fullName = `${user.firstname || ''} ${user.lastname || ''}`.toLowerCase();
        const phone = (user.phone || '').toLowerCase();
        const tags = parseTags(user).join(' ').toLowerCase();
        const role = (user.role || '').toLowerCase();
        const birthdate = (user.birthdate || '').toLowerCase();

        return fullName.includes(search) ||
               phone.includes(search) ||
               tags.includes(search) ||
               role.includes(search) ||
               birthdate.includes(search);
      });
    }

    // Nach Jahr filtern
    if (!showInactive && yearFilter) {
      filtered = filtered.filter(user => {
        if (!user.workYears || user.workYears.length === 0) {
          // Wenn keine Arbeitsjahre definiert, aktuelles Jahr annehmen
          return yearFilter === new Date().getFullYear().toString();
        }
        return user.workYears.some(wy => 
          wy.year.toString() === yearFilter && wy.is_active
        );
      });
    }

    // Abgesagte Mitarbeiter ausblenden (manueller Tag oder Alt-Status)
    if (hideRejected) {
      filtered = filtered.filter(user =>
        !parseTags(user).includes('abgesagt') && user.status !== 'abgesagt');
    }

    // "Ohne jede Aktivität" ausblenden: weder manuelle Tags noch Vertrag/
    // Verfügbarkeit/Planung/Dienstplan-Nachricht vorhanden
    if (hideNochKeineRueckmeldung) {
      filtered = filtered.filter(user => {
        const ov = statusOverview[user.id] || {};
        return parseTags(user).length > 0 || ov.contract || ov.avail_days || ov.planned_shifts || ov.wa_sent_at;
      });
    }

    // Buchhalter-Status (pro Jahr): „angelegt" / „abgerechnet" – ja, nein oder egal
    const accMatch = (want, is) => want === 'all' || (want === 'yes') === !!is;
    if (accFilter.created !== 'all' || accFilter.billed !== 'all') {
      filtered = filtered.filter(user => {
        const ov = statusOverview[user.id] || {};
        return accMatch(accFilter.created, ov.acc_created) && accMatch(accFilter.billed, ov.acc_billed);
      });
    }

    // Sortierung anwenden
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;
        
        switch (sortConfig.key) {
          case 'name':
            aValue = `${a.lastname || ''} ${a.firstname || ''}`.trim().toLowerCase();
            bValue = `${b.lastname || ''} ${b.firstname || ''}`.trim().toLowerCase();
            break;
          case 'status':
            aValue = statusScore(a);
            bValue = statusScore(b);
            break;
          case 'birthdate':
            // Konvertiere ttmmyyyy zu einem sortierbaren Date
            const aBirthdate = a.birthdate || '01011900';
            const bBirthdate = b.birthdate || '01011900';
            aValue = new Date(formatBirthdateForInput(aBirthdate));
            bValue = new Date(formatBirthdateForInput(bBirthdate));
            break;
          case 'phone':
            aValue = (a.phone || '').toLowerCase();
            bValue = (b.phone || '').toLowerCase();
            break;
          case 'role':
            aValue = (a.role || '').toLowerCase();
            bValue = (b.role || '').toLowerCase();
            break;
          case 'work_permit':
            aValue = (a.work_permit_valid || '').toLowerCase();
            bValue = (b.work_permit_valid || '').toLowerCase();
            break;
          case 'cash_register':
            aValue = a.can_work_cash_register ? 1 : 0;
            bValue = b.can_work_cash_register ? 1 : 0;
            break;
          case 'performance':
            const performanceOrder = { 'schlecht': 0, 'normal': 1, 'gut': 2 };
            aValue = performanceOrder[a.performance_rating] || 1;
            bValue = performanceOrder[b.performance_rating] || 1;
            break;
          default:
            aValue = a[sortConfig.key] || '';
            bValue = b[sortConfig.key] || '';
        }
        
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      // Standard-Sortierung nach Status-Workflow wenn keine spezielle Sortierung gewählt
      filtered = sortUsersByStatus(filtered);
    }

    setFilteredUsers(filtered);
  };

  const fetchUserDocuments = async (userId) => {
    try {
      const data = await apiCall(`/documents?user_id=${userId}`);
      setUserDocuments(data);
    } catch (err) {
      console.error('Fehler beim Laden der Dokumente:', err);
      setUserDocuments([]);
    }
  };

  const fetchUserWorkYears = async (userId) => {
    try {
      const data = await apiCall(`/user-work-years/${userId}`);
      setUserWorkYears(data);
    } catch (err) {
      console.error('Fehler beim Laden der Arbeitsjahre:', err);
      setUserWorkYears([]);
    }
  };

  const fetchPartnerPreferences = async (userId) => {
    try {
      const data = await apiCall(`/users/${userId}/partner-preferences`);
      setPartnerPreferences(data);
    } catch (err) {
      console.error('Fehler beim Laden der Arbeitspartner-Wünsche:', err);
      setPartnerPreferences([]);
    }
  };

  const handleShowPartners = (user) => {
    setSelectedUser(user);
    setPartnerData({ preferred_partner_id: '', priority: 1, notes: '', bidirectional: true });
    fetchPartnerPreferences(user.id);
    setShowPartnerModal(true);
  };

  const handlePartnerSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await apiCall(`/users/${selectedUser.id}/partner-preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(partnerData)
      });
      
      
      
      // Reset form and refresh data
      setPartnerData({ preferred_partner_id: '', priority: 1, notes: '', bidirectional: true });
      fetchPartnerPreferences(selectedUser.id);
      
      // Zeige Erfolg-Nachricht basierend auf Bidirektionalität
      const result = res;
      if (result.bidirectional) {
        notify('Arbeitspartner-Wünsche bidirektional hinzugefügt! Beide Mitarbeiter haben jetzt eine Verbindung zueinander.');
      } else {
        notify('Arbeitspartner-Wunsch erfolgreich hinzugefügt!');
      }
    } catch (err) {
      console.error('Fehler beim Hinzufügen des Arbeitspartners:', err);
      notify('Fehler beim Hinzufügen des Arbeitspartners');
    }
  };

  const handleDeletePartner = async (partnerId) => {
    if (!await confirmDialog('Möchten Sie diesen Arbeitspartner-Wunsch wirklich entfernen?')) return;
    
    try {
      const res = await apiCall(`/users/${selectedUser.id}/partner-preferences/${partnerId}`, {
        method: 'DELETE'
      });
      
      fetchPartnerPreferences(selectedUser.id);
      notify('Arbeitspartner-Wunsch erfolgreich entfernt!');
    } catch (err) {
      console.error('Fehler beim Löschen des Arbeitspartners:', err);
      notify('Fehler beim Löschen des Arbeitspartners');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🚀 handleSubmit gestartet');
    console.log('📝 FormData:', formData);
    console.log('🔄 EditingUser:', editingUser);
    
    try {
      const url = editingUser 
        ? `/users/${editingUser.id}`
        : '/users';
      const method = editingUser ? 'PUT' : 'POST';
      
      // Transform formData for backend compatibility
      const backendData = {
        ...formData,
        role_id: formData.role === 'admin' ? 1 : formData.role === 'lead' ? 3 : formData.role === 'planner' ? 4 : 2, // Convert role to role_id (3 = Einsatzleitung, 4 = Stations-Planer)
        birthdate: formatBirthdateForDatabase(formData.birthdate), // Convert birthdate format
      };
      delete backendData.role; // Remove the old role field
      
      console.log(`📡 Request: ${method} ${url}`);
      console.log('📦 Body:', JSON.stringify(backendData));
      
      const userData = await apiCall(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(backendData)
      });
      
      console.log('✅ User erstellt/updated:', userData);
      
      await fetchUsers();
      setShowModal(false);
      setEditingUser(null);
      setFormData({
        firstname: '', lastname: '', birthdate: '', password: '', role: 'mitarbeiter',
        phone: '', email: '', work_permit_valid: 'not_required', current_year_active: true,
        work_years: new Date().getFullYear().toString(), can_work_cash_register: false, can_work_wiesngarten: false, performance_rating: 'normal'
      });
      
      // Füge aktuelles Jahr als Arbeitsjahr hinzu für neue Mitarbeiter
      if (!editingUser && userData && userData.id) {
        try {
          console.log('📅 Füge Arbeitsjahr hinzu für User:', userData.id);
          const workYearResult = await addWorkYear(userData.id, new Date().getFullYear(), true, 'Neu erstellt');
          console.log('✅ Arbeitsjahr erfolgreich hinzugefügt:', workYearResult);
        } catch (workYearError) {
          console.warn('⚠️ Arbeitsjahr konnte nicht hinzugefügt werden:', workYearError);
          // Zeige Warnung, aber breche nicht ab - Mitarbeiter wurde erstellt
          notify(`Mitarbeiter wurde erstellt, aber Arbeitsjahr konnte nicht hinzugefügt werden: ${workYearError.message}`);
        }
      }
      
      console.log('🎉 Vorgang erfolgreich abgeschlossen');
    } catch (err) {
      console.error('❌ Fehler beim Speichern:', err);
      notify(`Netzwerk-Fehler beim Speichern: ${err.message}`);
    }
  };

  const handleDocumentSubmit = async (e) => {
    e.preventDefault();
    
    if (!documentFile) {
      notify('Bitte wählen Sie eine Datei aus');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', documentFile);
      formData.append('user_id', selectedUser.id);
      formData.append('document_type', documentData.document_type);
      formData.append('status', documentData.status);
      formData.append('notes', documentData.notes);
      formData.append('year', documentData.year || new Date().getFullYear());

      const res = await apiCall(`/documents/upload`, {
        method: 'POST',
        body: formData
      });

      // apiCall() parsiert automatisch JSON bei erfolgreichen Antworten
      await fetchUserDocuments(selectedUser.id);
      await fetchDocuments(); // Aktualisiere auch die Haupttabelle
      setDocumentFile(null);
      setDocumentData({ document_type: 'arbeitsvertrag', status: 'genehmigt', notes: '', year: new Date().getFullYear() });
      // Modal nicht schließen, um weitere Dokumente hinzuzufügen
      notify('Dokument erfolgreich hochgeladen!');
    } catch (err) {
      console.error('Fehler beim Upload:', err);
      notify('Fehler beim Upload: ' + err.message);
    }
  };

  const addWorkYear = async (userId, year, isActive, notes = '') => {
    try {
      console.log('🔄 Adding work year:', { userId, year, isActive, notes });
      
      const response = await apiCall(`/user-work-years/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, year, is_active: isActive, notes })
      });
      
      console.log('✅ Work year response:', response);

      await fetchUsers();
      if (selectedUser && selectedUser.id === userId) {
        await fetchUserWorkYears(userId);
      }
      
      return response;
    } catch (err) {
      console.error('❌ Fehler beim Hinzufügen des Arbeitsjahres:', err);
      console.error('📋 Error details:', {
        message: err.message,
        stack: err.stack,
        userId,
        year,
        isActive,
        notes
      });
      throw err; // Re-throw für bessere Fehlerbehandlung
    }
  };

  const handleWorkYearSubmit = async (e) => {
    e.preventDefault();
    await addWorkYear(selectedUser.id, workYearData.year, workYearData.is_active, workYearData.notes);
    setWorkYearData({ year: new Date().getFullYear(), is_active: true, notes: '' });
  };

  // Funktion zum Generieren und Kopieren des direkten Login-Links
  const copyLoginLink = async (user) => {
    try {
      // Erstelle den direkten Login-Link mit Geburtsdatum als Passwort
      const baseUrl = window.location.origin;
      const loginData = {
        username: user.lastname || '', // Nur Nachname für Login!
        password: user.birthdate || '' // Geburtsdatum als Passwort
      };
      
      // Erstelle den Link mit automatischem Login
      const loginLink = `${baseUrl}/?autoLogin=${encodeURIComponent(JSON.stringify(loginData))}`;
      
      console.log('🔍 Debug Info:');
      console.log('- navigator.clipboard verfügbar:', !!navigator.clipboard);
      console.log('- window.isSecureContext:', window.isSecureContext);
      console.log('- Login-Daten:', loginData);
      console.log('- Link to copy:', loginLink);
      
      let copySuccess = false;
      let method = '';
      
      // Methode 1: Moderne Clipboard API (nur bei HTTPS)
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(loginLink);
          copySuccess = true;
          method = 'navigator.clipboard';
          console.log('✅ Kopiert mit navigator.clipboard API');
        } catch (clipboardError) {
          console.log('❌ navigator.clipboard fehlgeschlagen:', clipboardError);
        }
      }
      
      // Methode 2: Fallback mit execCommand
      if (!copySuccess) {
        try {
          const textArea = document.createElement('textarea');
          textArea.value = loginLink;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          
          const success = document.execCommand('copy');
          document.body.removeChild(textArea);
          
          if (success) {
            copySuccess = true;
            method = 'document.execCommand';
            console.log('✅ Kopiert mit document.execCommand');
          } else {
            console.log('❌ document.execCommand fehlgeschlagen');
          }
        } catch (execError) {
          console.log('❌ execCommand Fehler:', execError);
        }
      }
      
      // Methode 3: Manual copy prompt als letzte Option
      if (!copySuccess) {
        method = 'manual';
        console.log('📋 Zeige manuellen Kopier-Dialog');
        
        // Erstelle einen Dialog mit dem Link zum manuellen Kopieren
        const copyDialog = document.createElement('div');
        copyDialog.style.cssText = `
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: white;
          border: 2px solid #007bff;
          border-radius: 10px;
          padding: 20px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.3);
          z-index: 10001;
          max-width: 500px;
          font-family: Arial, sans-serif;
        `;
        
        const displayName = user.firstname && user.lastname 
          ? `${user.firstname} ${user.lastname}` 
          : user.lastname || user.name || '';
        
        copyDialog.innerHTML = `
          <div style="margin-bottom: 15px; font-weight: bold; color: #007bff;">
            📋 Login-Link für ${displayName}
          </div>
          <div style="margin-bottom: 15px; font-size: 14px; color: #666;">
            Bitte kopieren Sie den Link manuell:
          </div>
          <textarea style="width: 100%; height: 80px; font-size: 12px; border: 1px solid #ccc; padding: 5px; border-radius: 3px;" readonly>${loginLink}</textarea>
          <div style="margin-top: 15px; text-align: center;">
            <button onclick="this.parentElement.parentElement.remove()" style="background: #007bff; color: white; border: none; padding: 10px 20px; border-radius: 5px; cursor: pointer;">
              Schließen
            </button>
          </div>
        `;
        
        document.body.appendChild(copyDialog);
        
        // Auto-select den Text im Textfeld
        const textarea = copyDialog.querySelector('textarea');
        textarea.focus();
        textarea.select();
        
        return; // Beende die Funktion hier
      }
      
      // Zeige Erfolgs-Toast
      const displayName = user.firstname && user.lastname 
        ? `${user.firstname} ${user.lastname}` 
        : user.lastname || user.name || '';
      showToast(`✅ Login-Link für ${displayName} kopiert (${method})!`, 'success');
      console.log(`🎉 Link erfolgreich kopiert mit Methode: ${method}`);
      
    } catch (error) {
      console.error('🚨 Fehler beim Kopieren des Links:', error);
      showToast('❌ Fehler beim Kopieren des Links', 'error');
    }
  };

  // Einfache Toast-Nachricht Funktion
  const showToast = (message, type = 'info') => {
    // Erstelle Toast Element
    const toast = document.createElement('div');
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 12px 20px;
      border-radius: 5px;
      color: white;
      font-weight: bold;
      z-index: 10000;
      max-width: 300px;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
      ${type === 'success' ? 'background-color: #28a745;' : 'background-color: #dc3545;'}
    `;
    
    document.body.appendChild(toast);
    
    // Entferne Toast nach 3 Sekunden
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 3000);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      firstname: user.firstname || '',
      lastname: user.lastname || '',
      birthdate: formatBirthdateForInput(user.birthdate),
      role: user.role,
      phone: user.phone || '',
      email: user.email || '',
      work_permit_valid: user.work_permit_valid || 'not_required',
      current_year_active: user.current_year_active,
      work_years: user.work_years || new Date().getFullYear().toString(),
      can_work_cash_register: user.can_work_cash_register || false,
      can_work_wiesngarten: user.can_work_wiesngarten || false,
      performance_rating: user.performance_rating || 'normal'
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (await confirmDialog('Mitarbeiter wirklich löschen?')) {
      try {
        await apiCall(`/users/${id}`, {
          method: 'DELETE'
        });
        await fetchUsers();
      } catch (err) {
        console.error('Fehler beim Löschen:', err);
        notify('Fehler beim Löschen: ' + err.message);
      }
    }
  };

  const handleAccountantStatusUpdate = async (userId, field, value) => {
    try {
      // Buchhalter-Status gilt PRO JAHR (das gerade gefilterte Jahr)
      const payload = { year: parseInt(yearFilter, 10) || new Date().getFullYear() };
      if (field === 'employee_created') {
        payload.employee_created = value;
      } else if (field === 'employee_billed') {
        payload.employee_billed = value;
      } else {
        throw new Error(`Unbekanntes Feld: ${field}`);
      }

      await apiCall(`/users/${userId}/accountant-status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      // Nur die abgeleitete Übersicht aktualisieren (kein teurer Komplett-Reload)
      setStatusOverview((prev) => ({
        ...prev,
        [userId]: {
          ...(prev[userId] || {}),
          [field === 'employee_created' ? 'acc_created' : 'acc_billed']: value,
        },
      }));
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Buchhalter-Status:', error);
      throw error; // Weiterleiten für Rollback in Komponente
    }
  };

  const handleApprovedToggle = async (userId, currentApproved) => {
    try {
      await apiCall(`/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved: !currentApproved })
      });
      await fetchUsers();
    } catch (error) {
      console.error('Fehler beim Ändern der Freischaltung:', error);
    }
  };

  const handlePerformanceRatingUpdate = async (userId, field, value) => {
    try {
      const payload = {};
      payload[field] = value;
      
      await apiCall(`/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      // Users neu laden, um die Änderung zu reflektieren
      await fetchUsers();
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Mitarbeiterbewertung:', error);
      throw error; // Weiterleiten für Rollback in Komponente
    }
  };

  const deleteDocument = async (docId) => {
    if (await confirmDialog('Dokument wirklich löschen?')) {
      try {
        await apiCall(`/user-documents/${docId}`, {
          method: 'DELETE'
        });
        await fetchUserDocuments(selectedUser.id);
        await fetchDocuments(); // Aktualisiere auch die Haupttabelle
      } catch (err) {
        console.error('Fehler beim Löschen des Dokuments:', err);
        notify('Fehler beim Löschen des Dokuments: ' + err.message);
      }
    }
  };

  const deleteWorkYear = async (workYear) => {
    if (await confirmDialog('Arbeitsjahr wirklich löschen?')) {
      try {
        await apiCall(`/user-work-years/${selectedUser.id}/${workYear.year}`, {
          method: 'DELETE'
        });
        await fetchUserWorkYears(selectedUser.id);
        await fetchUsers();
      } catch (err) {
        console.error('Fehler beim Löschen des Arbeitsjahres:', err);
      }
    }
  };

  const toggleWorkYearActive = async (workYear, currentStatus) => {
    try {
      await apiCall(`/user-work-years/${selectedUser.id}/${workYear.year}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !currentStatus })
      });
      await fetchUserWorkYears(selectedUser.id);
      await fetchUsers();
    } catch (err) {
      console.error('Fehler beim Ändern des Arbeitsjahr-Status:', err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'genehmigt':
      case 'approved': return 'success';
      case 'ausstehend':
      case 'pending': return 'warning';
      case 'abgelehnt':
      case 'rejected': return 'danger';
      default: return 'secondary';
    }
  };

  const getDocumentTypeLabel = (type) => {
    const types = {
      'arbeitsvertrag': 'Arbeitsvertrag',
      'ausweis': 'Personalausweis',
      'arbeitserlaubnis': 'Arbeitserlaubnis',
      'gesundheitszeugnis': 'Gesundheitszeugnis',
      'steuer': 'Steuerdokumente',
      'sonstiges': 'Sonstiges'
    };
    return types[type] || type;
  };

  // Fortschritts-Score für die Sortierung: Abgesagte zuerst (wie bisher),
  // danach aufsteigend nach tatsächlichem Fortschritt (automatisch abgeleitet).
  const statusScore = (u) => {
    const tags = parseTags(u);
    if (tags.includes('abgesagt') || u.status === 'abgesagt') return 0;
    const ov = statusOverview[u.id] || {};
    if (ov.wa_confirmed_at) return 8;
    if (ov.planned_shifts) return 7;
    if (ov.avail_days) return 6;
    if (ov.contract === 'approved') return 5;
    if (ov.contract === 'submitted') return 4;
    if (tags.includes('zugesagt')) return 3;
    if (tags.includes('kontaktiert')) return 2;
    return 1;
  };

  // Automatisch abgeleitete Status-Badges für die Tabelle
  const autoBadges = (u) => {
    const ov = statusOverview[u.id] || {};
    const b = [];
    if (ov.contract === 'approved') b.push({ t: '📄 Vertrag ✓', c: 'success' });
    else if (ov.contract === 'submitted') b.push({ t: '📄 eingereicht', c: 'info' });
    else if (ov.contract === 'rejected') b.push({ t: '📄 abgelehnt', c: 'danger', title: ov.rejection_reason || 'Vertrag abgelehnt' });
    else if (ov.contract === 'draft') b.push({ t: '📄 Entwurf', c: 'warning' });
    else b.push({ t: '📄 kein Vertrag', c: 'secondary' });
    if (ov.avail_days) b.push({ t: `🗓️ ${ov.avail_days} Tage`, c: 'light' });
    if (ov.planned_shifts) b.push({ t: `📋 ${ov.planned_shifts} Schichten`, c: 'primary' });
    if (ov.wa_confirmed_at) b.push({ t: '✅ Dienstplan bestätigt', c: 'success' });
    else if (ov.wa_problem_at) b.push({ t: '✋ Problem gemeldet', c: 'danger' });
    else if (ov.wa_sent_at) {
      const dl = ov.wa_read_at ? 'gelesen' : ov.wa_delivered_at ? 'zugestellt' : ov.wa_failed_at ? 'NICHT zugestellt' : 'gesendet';
      b.push({ t: `⏳ keine Antwort · ${dl}${ov.wa_reminded_at ? ' · erinnert' : ''}`, c: ov.wa_failed_at && !ov.wa_delivered_at ? 'danger' : 'warning' });
    }
    if (ov.wa_optout) b.push({ t: '🚫 WhatsApp abbestellt', c: 'secondary' });
    // Sozialleistungen: springt ins Auge, weil der Verdienst angerechnet wird
    if (ov.social_benefit) {
      const label = { grundsicherung: 'Grundsicherung', buergergeld: 'Bürgergeld', arbeitslosengeld: 'Arbeitslosengeld',
        rente: 'Rente', bafoeg: 'BAföG', wohngeld: 'Wohngeld' }[ov.social_benefit] || ov.social_benefit;
      b.push({ t: `⚠️ ${label}`, c: 'danger', title: `Bezieht ${label} – Verdienst wird angerechnet, muss vorher gemeldet werden!` });
    }
    return b;
  };

  // Sortiere Benutzer nach Fortschritt (abgesagt zuerst, dann aufsteigend)
  const sortUsersByStatus = (users) => {
    return users.sort((a, b) => {
      const sA = statusScore(a);
      const sB = statusScore(b);
      if (sA !== sB) return sA - sB;
      const nameA = `${a.lastname || ''} ${a.firstname || ''}`.trim();
      const nameB = `${b.lastname || ''} ${b.firstname || ''}`.trim();
      return nameA.localeCompare(nameB);
    });
  };

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col">
          <h2><i className="bi bi-people-fill me-2"></i>Mitarbeiterverwaltung</h2>
        </div>
        <div className="col-auto">
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <i className="bi bi-person-plus-fill me-2"></i>Neuer Mitarbeiter
          </button>
        </div>
      </div>

      {/* Status Statistics */}
      <div className="row mb-3">
        <div className="col">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">📊 Status-Übersicht</h5>
              <div className="row">
                {(() => {
                  // Fortschritts-Stufen (automatisch abgeleitet): jeder zählt in seiner höchsten Stufe
                  const STAGES = [
                    { score: 0, icon: '❌', label: 'Abgesagt', border: 'danger' },
                    { score: 1, icon: '📝', label: 'Ohne Rückmeldung', border: 'secondary' },
                    { score: 2, icon: '💬', label: 'Kontaktiert', border: 'primary' },
                    { score: 3, icon: '👍', label: 'Zugesagt', border: 'success' },
                    { score: 4, icon: '📄', label: 'Vertrag eingereicht', border: 'info' },
                    { score: 5, icon: '📄', label: 'Vertrag genehmigt', border: 'success' },
                    { score: 6, icon: '🗓️', label: 'Verfügbarkeit da', border: 'primary' },
                    { score: 7, icon: '📋', label: 'Geplant', border: 'primary' },
                    { score: 8, icon: '✅', label: 'Dienstplan bestätigt', border: 'success' },
                  ];
                  const total = filteredUsers.length;
                  const counts = {};
                  filteredUsers.forEach((u) => { const s = statusScore(u); counts[s] = (counts[s] || 0) + 1; });
                  return STAGES.filter((s) => counts[s.score]).map((s) => {
                    const count = counts[s.score];
                    const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                    return (
                      <div key={s.score} className="col-md-2 col-6 mb-2">
                        <div className={`card border-${s.border}`}>
                          <div className="card-body text-center p-2">
                            <div style={{ fontSize: '24px' }}>{s.icon}</div>
                            <div className="fw-bold">{count} ({percentage}%)</div>
                            <div className="small text-muted">{s.label}</div>
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter + Schnell-Aktionen: eine kompakte Zeile */}
      <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
        <select
          className="form-select form-select-sm"
          style={{ width: 90 }}
          value={yearFilter}
          onChange={(e) => setYearFilter(e.target.value)}
          title="Jahr filtern"
        >
          {[new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map(y => (
            <option key={y} value={y.toString()}>{y}</option>
          ))}
        </select>
        <input
          type="text"
          className="form-control form-control-sm"
          style={{ maxWidth: 240 }}
          placeholder="🔍 Suchen…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="form-check form-check-inline m-0">
          <input className="form-check-input" type="checkbox" id="showInactive"
            checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          <label className="form-check-label small" htmlFor="showInactive">auch Inaktive</label>
        </div>
        <div className="form-check form-check-inline m-0">
          <input className="form-check-input" type="checkbox" id="hideRejected"
            checked={hideRejected} onChange={(e) => setHideRejected(e.target.checked)} />
          <label className="form-check-label small text-danger" htmlFor="hideRejected">Abgesagte ausblenden</label>
        </div>
        <div className="form-check form-check-inline m-0">
          <input className="form-check-input" type="checkbox" id="hideNochKeineRueckmeldung"
            checked={hideNochKeineRueckmeldung} onChange={(e) => setHideNochKeineRueckmeldung(e.target.checked)} />
          <label className="form-check-label small text-warning" htmlFor="hideNochKeineRueckmeldung">ohne Aktivität ausblenden</label>
        </div>
        {user && user.role === 'admin' && (
          <div className="d-flex align-items-center gap-1" title="Buchhalter-Status im gewählten Jahr">
            <span className="small text-muted">🚦</span>
            <select className="form-select form-select-sm" style={{ width: 'auto' }}
              value={accFilter.created} onChange={(e) => setAccFilter({ ...accFilter, created: e.target.value })}>
              <option value="all">Angelegt: alle</option>
              <option value="yes">✅ angelegt</option>
              <option value="no">⬜ nicht angelegt</option>
            </select>
            <select className="form-select form-select-sm" style={{ width: 'auto' }}
              value={accFilter.billed} onChange={(e) => setAccFilter({ ...accFilter, billed: e.target.value })}>
              <option value="all">Abgerechnet: alle</option>
              <option value="yes">💶 abgerechnet</option>
              <option value="no">⬜ nicht abgerechnet</option>
            </select>
          </div>
        )}
        {user && user.role === 'admin' && (
          <div className="btn-group ms-auto" role="group">
            <button className="btn btn-outline-primary btn-sm"
              onClick={() => handleBulkTag('kontaktiert')}
              title={`Alle ${filteredUsers.length} sichtbaren Mitarbeiter zusätzlich als 'kontaktiert' markieren`}>
              💬 Alle → Kontaktiert
            </button>
            <button className="btn btn-outline-secondary btn-sm"
              onClick={() => handleBulkTag(null)}
              title={`Manuelle Markierungen aller ${filteredUsers.length} sichtbaren Mitarbeiter entfernen`}>
              📝 Zurücksetzen
            </button>
          </div>
        )}
      </div>

      {docPreview && (
        <DocView src={docPreview.url} mime={docPreview.mime} alt={docPreview.alt} autoOpen hideThumb
          onClose={() => { try { URL.revokeObjectURL(docPreview.url); } catch (_) { /* egal */ } setDocPreview(null); }} />
      )}

      {/* Users Table – kein eigener Container mehr, die Seite scrollt normal */}
      <div>
        <div>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="card-title mb-0">
              Mitarbeiter-Übersicht
              {searchTerm && (
                <small className="text-muted ms-2">
                  (gefiltert nach: "{searchTerm}")
                </small>
              )}
            </h5>
            <div className="text-muted">
              <strong>{filteredUsers.length}</strong> von <strong>{users.length}</strong> Mitarbeitern
              {sortConfig.key && (
                <small className="ms-2">
                  • sortiert nach {
                    sortConfig.key === 'name' ? 'Name' :
                    sortConfig.key === 'status' ? 'Status' :
                    sortConfig.key === 'birthdate' ? 'Geburtsdatum' :
                    sortConfig.key === 'phone' ? 'Telefon' :
                    sortConfig.key === 'role' ? 'Rolle' :
                    sortConfig.key === 'work_permit' ? 'Arbeitserlaubnis' :
                    sortConfig.key === 'cash_register' ? 'Kasse' :
                    sortConfig.key === 'performance' ? 'Bewertung' :
                    sortConfig.key
                  } {sortConfig.direction === 'asc' ? '↑' : '↓'}
                </small>
              )}
            </div>
          </div>
          
          {/* Die dunkle Spaltenleiste dockt beim Seiten-Scrollen oben an. Wichtig:
              WheelchairAdmin.css setzt global ".table { overflow: hidden }" – das
              bricht position:sticky, daher hier explizit overflow: visible.
              Schriftgröße skaliert mit der Bildschirmbreite (Buttons bleiben klein). */}
          <style>{`
            .um-table { overflow: visible !important; font-size: clamp(13px, 1vw, 16px); width: 100%; }
            .um-table thead th { position: sticky; top: ${navH}px; z-index: 15; background: #212529; color: #fff; white-space: nowrap; box-shadow: 0 2px 4px rgba(0,0,0,.15); }
            .um-table th, .um-table td { padding: .3rem .7rem; vertical-align: middle; }
            .um-col-tight { white-space: nowrap; }
            .um-col-name { min-width: 170px; }
          `}</style>
          <div>
            <table className="table table-hover table-sm align-middle um-table">
              <thead>
                <tr>
                  <th className="um-col-name" style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>
                    Name{getSortIcon('name')}
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>
                    Status{getSortIcon('status')}
                  </th>
                  <th className="d-none d-xl-table-cell um-col-tight" style={{ cursor: 'pointer' }} onClick={() => handleSort('birthdate')}>
                    Geboren{getSortIcon('birthdate')}
                  </th>
                  <th className="um-col-tight" title="🪪 Ausweis · 📄 Vertrag · 💼 Arbeitserlaubnis · 🏠 Aufenthaltstitel">Dokumente</th>
                  <th className="um-col-tight" style={{ cursor: 'pointer' }} onClick={() => handleSort('cash_register')}>
                    Kasse{getSortIcon('cash_register')}
                  </th>
                  {user && user.role === 'admin' && (
                    <th className="um-col-tight" style={{ cursor: 'pointer' }} onClick={() => handleSort('performance')} title="Bewertung (Ampel anklicken zum Ändern)">
                      ⭐{getSortIcon('performance')}
                    </th>
                  )}
                  <th className="um-col-tight" title="Erster und letzter Einsatz – für Anmeldung / Abmeldung">Einsatz</th>
                  <th className="d-none d-lg-table-cell um-col-tight">Jahre</th>
                  {user && user.role === 'admin' && (
                    <th className="um-col-tight">🚦 Buchhalter</th>
                  )}
                  <th className="um-col-tight">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(currentUser => (
                  <tr key={currentUser.id}>
                    <td>
                      <strong>
                        {currentUser.firstname && currentUser.lastname 
                          ? `${currentUser.firstname} ${currentUser.lastname}` 
                          : currentUser.name || 'Unbekannt'}
                      </strong>
                      {!currentUser.approved && (
                        <span className="badge bg-info ms-2" title="Neu registriert, noch nicht freigeschaltet">🆕 NEU</span>
                      )}
                      {!showInactive && currentUser.workYears && !currentUser.workYears.some(wy => 
                        wy.year.toString() === yearFilter && wy.is_active
                      ) && (
                        <span className="badge bg-secondary ms-2">Inaktiv {yearFilter}</span>
                      )}
                    </td>
                    <td>
                      {/* Eine Zeile: Icon-Chips (💬👍❌, aktiv = farbig) + automatische Badges */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
                        {MANUAL_TAGS.map((tag) => {
                          const active = parseTags(currentUser).includes(tag.key);
                          if (!(user && user.role === 'admin')) {
                            return active ? (
                              <span key={tag.key} className={`badge bg-${tag.badge}`} style={{ fontSize: '0.85em' }}>
                                {tag.icon} {tag.label}
                              </span>
                            ) : null;
                          }
                          return (
                            <button
                              key={tag.key}
                              className={`btn btn-sm ${active ? 'btn-' + tag.badge : 'btn-' + tag.btn}`}
                              style={{ fontSize: '0.85em', padding: '1px 7px', lineHeight: 1.6,
                                opacity: active ? 1 : 0.45, filter: active ? 'none' : 'grayscale(50%)' }}
                              onClick={() => handleTagToggle(currentUser, tag.key)}
                              title={active ? `„${tag.label}" entfernen` : `Als „${tag.label}" markieren`}
                            >
                              {tag.icon} {tag.label}
                            </button>
                          );
                        })}
                        <span style={{ width: 4 }} />
                        {autoBadges(currentUser).map((b, i) => (
                          <span
                            key={i}
                            className={`badge ${b.c === 'light' ? 'bg-light text-dark border' : 'bg-' + b.c}${b.c === 'warning' ? ' text-dark' : ''}`}
                            style={{ fontSize: '0.8em' }}
                            title={b.title || ''}
                          >
                            {b.t}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="d-none d-xl-table-cell">{formatBirthdateForDisplay(currentUser.birthdate)}</td>
                    <td>{renderDocsCell(currentUser)}</td>
                    <td>
                      <span className={`badge bg-${currentUser.can_work_cash_register ? 'success' : 'secondary'}`}>
                        {currentUser.can_work_cash_register ? '✓ Ja' : '✗ Nein'}
                      </span>
                    </td>
                    {user && user.role === 'admin' && (
                      <td>
                        <CompactPerformanceRating
                          user={currentUser}
                          onUpdate={handlePerformanceRatingUpdate}
                        />
                      </td>
                    )}

                    {/* Erster / letzter Einsatz übereinander – fürs An- und Abmelden
                        (kurzfristige Beschäftigung), bewusst schmal gehalten */}
                    <td className="um-col-tight" title="Erster Einsatz (Anmeldung) / letzter Einsatz (Abmeldung)">
                      {(() => {
                        const so = statusOverview[currentUser.id] || {};
                        const f = (d) => (d ? `${d.slice(8, 10)}.${d.slice(5, 7)}.` : '–');
                        if (!so.first_shift) return <span className="text-muted">–</span>;
                        return (
                          <div style={{ fontSize: '0.78em', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                            <div><span className="text-muted">an</span> {f(so.first_shift)}</div>
                            <div><span className="text-muted">ab</span> {f(so.last_shift)}</div>
                          </div>
                        );
                      })()}
                    </td>

                    <td className="d-none d-lg-table-cell um-col-tight">
                      {currentUser.workYears && currentUser.workYears.length > 0 ? (() => {
                        const sorted = [...currentUser.workYears].sort((a, b) => b.year - a.year);
                        const shown = sorted.slice(0, 2);
                        const rest = sorted.slice(2);
                        return (
                          <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            {shown.map(wy => (
                              <span key={wy.id} className={`badge bg-${wy.is_active ? 'success' : 'secondary'}`} style={{ fontSize: '0.8em' }}>
                                {wy.year}
                              </span>
                            ))}
                            {rest.length > 0 && (
                              <span className="badge bg-light text-dark border" style={{ fontSize: '0.8em' }}
                                title={rest.map(wy => `${wy.year}${wy.is_active ? '' : ' (inaktiv)'}`).join(', ')}>
                                +{rest.length}
                              </span>
                            )}
                          </div>
                        );
                      })() : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    {user && user.role === 'admin' && (
                      <td>
                        <AccountantTrafficLight
                          user={{
                            ...currentUser,
                            // Buchhalter-Status kommt jetzt PRO JAHR aus der Status-Übersicht
                            accountant_employee_created: !!(statusOverview[currentUser.id] || {}).acc_created,
                            accountant_employee_billed: !!(statusOverview[currentUser.id] || {}).acc_billed,
                          }}
                          onUpdate={handleAccountantStatusUpdate}
                        />
                      </td>
                    )}
                    {/* Freischaltung-Spalte entfernt: neue Konten sind immer direkt freigeschaltet */}
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                        {/* Erste Zeile: Bearbeiten, Dokumente, Arbeitsjahre, Arbeitspartner-Wünsche */}
                        <div style={{ display: 'flex', gap: '3px' }}>
                          <button 
                            className="btn btn-primary btn-sm"
                            style={{ padding: '4px 8px', fontSize: '13px' }}
                            onClick={() => handleEdit(currentUser)}
                            title="Bearbeiten"
                          >
                            <i className="bi bi-pencil-fill"></i>
                          </button>
                          <button 
                            className="btn btn-outline-info btn-sm"
                            style={{ padding: '4px 8px', fontSize: '13px' }}
                            onClick={() => {
                              setSelectedUser(currentUser);
                              fetchUserDocuments(currentUser.id);
                              setShowDocumentModal(true);
                            }}
                            title="Dokumente"
                          >
                            <i className="bi bi-file-earmark-text-fill"></i>
                          </button>
                          <button 
                            className="btn btn-outline-success btn-sm"
                            style={{ padding: '4px 8px', fontSize: '13px' }}
                            onClick={() => {
                              setSelectedUser(currentUser);
                              fetchUserWorkYears(currentUser.id);
                              setShowWorkYearModal(true);
                            }}
                            title="Arbeitsjahre"
                          >
                            <i className="bi bi-calendar-date-fill"></i>
                          </button>
                          <button 
                            className="btn btn-outline-warning btn-sm"
                            style={{ padding: '4px 8px', fontSize: '13px' }}
                            onClick={() => handleShowPartners(currentUser)}
                            title="Arbeitspartner-Wünsche"
                          >
                            <i className="bi bi-people-fill"></i>
                          </button>
                        </div>
                        {/* Zweite Zeile: Login-Link, Stundenlogbuch, WhatsApp Chat, Löschen */}
                        <div style={{ display: 'flex', gap: '3px' }}>
                          <button 
                            className="btn btn-outline-secondary btn-sm"
                            style={{ padding: '4px 8px', fontSize: '13px' }}
                            onClick={() => copyLoginLink(currentUser)}
                            title="Direkter Login-Link kopieren"
                          >
                            <i className="bi bi-link-45deg"></i>
                          </button>
                          <button 
                            className="btn btn-outline-secondary btn-sm"
                            style={{ padding: '4px 8px', fontSize: '13px' }}
                            onClick={() => {
                              const url = `/employee-logs/${currentUser.id}/${encodeURIComponent(currentUser.firstname + ' ' + currentUser.lastname)}`;
                              window.open(url, '_blank');
                            }}
                            title="Stundenlogbuch"
                          >
                            <i className="bi bi-clock"></i>
                          </button>
                          <button 
                            className="btn btn-outline-success btn-sm"
                            style={{ padding: '4px 8px', fontSize: '13px' }}
                            onClick={() => openWhatsAppChat(currentUser)}
                            title="WhatsApp Chat"
                          >
                            💬
                          </button>
                          <button 
                            className="btn btn-outline-danger btn-sm"
                            style={{ padding: '4px 8px', fontSize: '13px' }}
                            onClick={() => handleDelete(currentUser.id)}
                            title="Löschen"
                          >
                            <i className="bi bi-trash-fill"></i>
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Vertragsdaten-Modal (Buchhalter-Sicht) */}
      {contractView && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setContractView(null)}>
          <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">📄 Vertragsdaten – {contractView.name}
                  <span className="badge bg-secondary ms-2">{contractView.statusLabel}</span>
                </h5>
                <button type="button" className="btn-close" onClick={() => setContractView(null)}></button>
              </div>
              <div className="modal-body">
                {contractView.rejection && (
                  <div className="alert alert-danger py-2">Ablehnungsgrund: {contractView.rejection}</div>
                )}
                {(() => {
                  // Zeilen einmal berechnen – für Tabelle UND „Alles kopieren"
                  const rows = [
                      ['Name (laut Vertrag)', contractView.d.employeeName],
                      ['Geburtsdatum', fmtBirthDE(contractView.d.employeeBirthdate)],
                      ['Geburtsort', contractView.d.birthPlace],
                      ['Staatsangehörigkeit', contractView.d.employeeNationality],
                      ['Adresse', contractView.d.employeeAddress],
                      ['Sozialversicherungs-Nr.', contractView.d.socialSecurityNumber],
                      ['Steuer-ID', contractView.d.taxId],
                      ['IBAN', contractView.d.iban],
                      ['Kontoinhaber', contractView.d.accountHolder],
                      ['Sozialleistungen ⚠️', contractView.d.socialBenefitStatus],
                      ['Beschäftigung', `${contractView.d.startDate || '?'} – ${contractView.d.endDate || '?'}`],
                      ['Lohn', contractView.d.salary ? `${contractView.d.salary} €/Std.` : ''],
                      ['Weitere kurzfristige Beschäftigungen (dieses Jahr)', (() => {
                        const jobs = (contractView.d.previousJobs || []).filter((j) => j && (j.employer || j.from || j.to));
                        if (!jobs.length) return 'Nein';
                        return jobs.map((j) => `${j.employer || '?'} (${j.from || '?'} – ${j.to || '?'}${j.totalDays ? `, ${j.totalDays} Tage` : ''})`).join('; ');
                      })()],
                      ['Eingereicht am', contractView.submitted ? new Date(contractView.submitted).toLocaleString('de-DE') : '—'],
                  ].map(([label, value]) => [label, String(value || '').trim()]);
                  const copy = async (text, key) => {
                    try {
                      if (navigator.clipboard && navigator.clipboard.writeText) await navigator.clipboard.writeText(text);
                      else { const ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); }
                      setCopiedKey(key); setTimeout(() => setCopiedKey(null), 1200);
                    } catch (_) { notify('Kopieren nicht möglich'); }
                  };
                  return (
                    <>
                      <div className="d-flex justify-content-end mb-2">
                        <button className="btn btn-sm btn-outline-secondary"
                          onClick={() => copy(rows.filter((r) => r[1]).map((r) => r[0] + ': ' + r[1]).join('\n'), '__all')}>
                          {copiedKey === '__all' ? '✓ kopiert' : '📋 Alles kopieren'}
                        </button>
                      </div>
                      <table className="table table-sm">
                        <tbody>
                          {rows.map(([label, value]) => (
                            <tr key={label}>
                              <th style={{ width: 220, fontWeight: 600 }}>{label}</th>
                              <td style={{ fontFamily: /Nr|IBAN|Steuer/.test(label) ? 'monospace' : 'inherit' }}>
                                {value || <span className="text-muted">—</span>}
                              </td>
                              <td style={{ width: 44 }} className="text-end">
                                {value && (
                                  <button className="btn btn-sm btn-outline-secondary py-0 px-1" title={`„${label}" kopieren`}
                                    onClick={() => copy(value, label)}>
                                    {copiedKey === label ? '✓' : '📋'}
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  );
                })()}
                {/* Buchhalter-Häkchen (dieselben Daten wie in der Tabelle, pro Jahr) */}
                {user && user.role === 'admin' && contractView.user && (
                  <div className="border rounded p-3 d-flex flex-wrap align-items-center gap-3 bg-light">
                    <strong className="me-2">🚦 Buchhalter</strong>
                    {[
                      { field: 'employee_created', key: 'acc_created', label: 'Angelegt', icon: '✅' },
                      { field: 'employee_billed', key: 'acc_billed', label: 'Abgerechnet', icon: '💶' },
                    ].map((b) => {
                      const on = !!(statusOverview[contractView.userId] || {})[b.key];
                      return (
                        <button key={b.field} type="button"
                          className={`btn btn-lg ${on ? 'btn-success' : 'btn-outline-secondary'}`}
                          style={{ minWidth: 190 }}
                          onClick={() => handleAccountantStatusUpdate(contractView.userId, b.field, !on).catch(() => notify('Fehler beim Speichern'))}
                          title={on ? `„${b.label}" zurücknehmen` : `Als „${b.label}" markieren`}>
                          {on ? '✓ ' : ''}{b.icon} {b.label}
                        </button>
                      );
                    })}
                  </div>
                )}
                <small className="text-muted d-block mt-2">Dokumente (Ausweis, Erlaubnisse) öffnest du über die Symbole in der Tabelle.</small>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setContractView(null)}>Schließen</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Modal */}
      {showModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingUser ? 'Mitarbeiter bearbeiten' : 'Neuer Mitarbeiter'}
                </h5>
                <button className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Vorname</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.firstname}
                        onChange={e => setFormData({...formData, firstname: e.target.value})}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Nachname</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.lastname}
                        onChange={e => setFormData({...formData, lastname: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Geburtsdatum</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.birthdate}
                      onChange={e => setFormData({...formData, birthdate: e.target.value})}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Rolle</label>
                    <select
                      className="form-select"
                      value={formData.role}
                      onChange={e => setFormData({...formData, role: e.target.value})}
                    >
                      <option value="mitarbeiter">Mitarbeiter</option>
                      <option value="lead">Einsatzleitung</option>
                      <option value="planner">Stations-Planer (Wiesn-Garten)</option>
                      <option value="admin">Administrator</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Telefon</label>
                    <input
                      type="tel"
                      className="form-control"
                      value={formData.phone}
                      onChange={e => setFormData({...formData, phone: e.target.value})}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">E-Mail <span className="text-muted">(für Benachrichtigungen)</span></label>
                    <input
                      type="email"
                      className="form-control"
                      value={formData.email}
                      onChange={e => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Arbeitserlaubnis</label>
                    <select
                      className="form-select"
                      value={formData.work_permit_valid}
                      onChange={e => setFormData({...formData, work_permit_valid: e.target.value})}
                    >
                      <option value="not_required">Nicht erforderlich (EU-Bürger)</option>
                      <option value="required">Erforderlich (Nicht-EU)</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="cashRegisterCheck"
                        checked={formData.can_work_cash_register}
                        onChange={e => setFormData({...formData, can_work_cash_register: e.target.checked})}
                      />
                      <label className="form-check-label" htmlFor="cashRegisterCheck">
                        Kann an der Kasse arbeiten
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="wiesngartenCheck"
                        checked={formData.can_work_wiesngarten || false}
                        onChange={e => setFormData({...formData, can_work_wiesngarten: e.target.checked})}
                      />
                      <label className="form-check-label" htmlFor="wiesngartenCheck">
                        🍺 Wiesn-Garten (durch Stations-Planer einplanbar)
                      </label>
                    </div>
                  </div>
                  {user && user.role === 'admin' && (
                    <div className="mb-3">
                      <label className="form-label">Mitarbeiterbewertung (nur für Admins)</label>
                      <select
                        className="form-select"
                        value={formData.performance_rating}
                        onChange={e => setFormData({...formData, performance_rating: e.target.value})}
                      >
                        <option value="gut">Gut</option>
                        <option value="normal">Normal</option>
                        <option value="schlecht">Schlecht</option>
                      </select>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Abbrechen
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingUser ? 'Aktualisieren' : 'Erstellen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Document Modal */}
      {showDocumentModal && selectedUser && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Dokumente - {selectedUser.firstname && selectedUser.lastname 
                    ? `${selectedUser.firstname} ${selectedUser.lastname}` 
                    : selectedUser.name || 'Benutzer'}
                </h5>
                <button className="btn-close" onClick={() => setShowDocumentModal(false)}></button>
              </div>
              <div className="modal-body">
                {/* Upload Form */}
                <div className="card mb-3">
                  <div className="card-header">
                    <h6 className="mb-0">Neues Dokument hochladen</h6>
                  </div>
                  <div className="card-body">
                    <form onSubmit={handleDocumentSubmit}>
                      <div className="row">
                        <div className="col-md-4">
                          <label className="form-label">Dokumenttyp</label>
                          <select
                            className="form-select"
                            value={documentData.document_type}
                            onChange={e => setDocumentData({...documentData, document_type: e.target.value})}
                          >
                            <option value="arbeitsvertrag">Arbeitsvertrag</option>
                            <option value="ausweis">Personalausweis</option>
                            <option value="arbeitserlaubnis">Arbeitserlaubnis</option>
                            <option value="gesundheitszeugnis">Gesundheitszeugnis</option>
                            <option value="steuer">Steuerdokumente</option>
                            <option value="sonstiges">Sonstiges</option>
                          </select>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Status</label>
                          <select
                            className="form-select"
                            value={documentData.status}
                            onChange={e => setDocumentData({...documentData, status: e.target.value})}
                          >
                            <option value="pending">Ausstehend</option>
                            <option value="approved">Genehmigt</option>
                            <option value="rejected">Abgelehnt</option>
                          </select>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Jahr</label>
                          <select
                            className="form-select"
                            value={documentData.year}
                            onChange={e => setDocumentData({...documentData, year: parseInt(e.target.value)})}
                          >
                            {[...Array(3)].map((_, i) => {
                              const y = new Date().getFullYear() - i;
                              return <option key={y} value={y}>{y}</option>;
                            })}
                          </select>
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Datei</label>
                        <input
                          type="file"
                          className="form-control"
                          onChange={e => setDocumentFile(e.target.files[0])}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Notizen</label>
                        <textarea
                          className="form-control"
                          rows="2"
                          value={documentData.notes}
                          onChange={e => setDocumentData({...documentData, notes: e.target.value})}
                        />
                      </div>
                      <button type="submit" className="btn btn-primary">
                        <i className="bi bi-upload me-2"></i>Hochladen
                      </button>
                    </form>
                  </div>
                </div>

                {/* Documents List */}
                {(() => {
                  const currentYear = new Date().getFullYear();
                  const currentYearDocs = userDocuments.filter(doc => {
                    const docYear = doc.year || new Date(doc.upload_date).getFullYear();
                    return docYear === currentYear;
                  });
                  const archiveDocs = userDocuments.filter(doc => {
                    const docYear = doc.year || new Date(doc.upload_date).getFullYear();
                    return docYear < currentYear;
                  });
                  // Group archive docs by year
                  const archiveByYear = {};
                  archiveDocs.forEach(doc => {
                    const y = doc.year || new Date(doc.upload_date).getFullYear();
                    if (!archiveByYear[y]) archiveByYear[y] = [];
                    archiveByYear[y].push(doc);
                  });
                  const archiveYears = Object.keys(archiveByYear).sort((a, b) => b - a);

                  const renderDocTable = (docs) => (
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Typ</th>
                            <th>Dateiname</th>
                            <th className="d-none d-lg-table-cell">Status</th>
                            <th>Datum</th>
                            <th>Aktionen</th>
                          </tr>
                        </thead>
                        <tbody>
                          {docs.map(doc => (
                            <tr key={doc.id}>
                              <td>
                                <div>
                                  <span className="d-none d-md-inline">
                                    {getDocumentTypeLabel(doc.document_type)}
                                  </span>
                                  <span className="d-md-none">
                                    {getDocumentTypeLabel(doc.document_type)?.length > 10 
                                      ? getDocumentTypeLabel(doc.document_type).substring(0, 10) + '...'
                                      : getDocumentTypeLabel(doc.document_type)}
                                  </span>
                                </div>
                              </td>
                              <td>
                                <div>
                                  <span className="d-none d-md-inline">
                                    {doc.original_name}
                                  </span>
                                  <span className="d-md-none">
                                    {doc.original_name?.length > 15 
                                      ? doc.original_name.substring(0, 15) + '...'
                                      : doc.original_name}
                                  </span>
                                  <div className="d-lg-none mt-1">
                                    <span className={`badge badge-sm bg-${getStatusColor(doc.status)}`} style={{ fontSize: '0.7rem' }}>
                                      {doc.status === 'ausstehend' || doc.status === 'pending' ? 'Ausstehend' :
                                       doc.status === 'genehmigt' || doc.status === 'approved' ? 'Genehmigt' : 'Abgelehnt'}
                                    </span>
                                  </div>
                                </div>
                              </td>
                              <td className="d-none d-lg-table-cell">
                                <span className={`badge bg-${getStatusColor(doc.status)}`}>
                                  {doc.status === 'ausstehend' || doc.status === 'pending' ? 'Ausstehend' :
                                   doc.status === 'genehmigt' || doc.status === 'approved' ? 'Genehmigt' : 'Abgelehnt'}
                                </span>
                              </td>
                              <td>
                                <small className="text-muted d-none d-md-inline">
                                  {new Date(doc.upload_date).toLocaleDateString()}
                                </small>
                                <small className="text-muted d-md-none">
                                  {new Date(doc.upload_date).toLocaleDateString('de-DE', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: '2-digit'
                                  })}
                                </small>
                              </td>
                              <td>
                                <div className="btn-group btn-group-sm d-none d-md-flex">
                                  <button
                                    onClick={() => handleViewDocument(doc.id, doc.user_id)}
                                    className="btn btn-outline-primary"
                                    title="Öffnen"
                                  >
                                    <i className="bi bi-eye-fill"></i>
                                  </button>
                                  <button
                                    className="btn btn-outline-danger"
                                    onClick={() => deleteDocument(doc.id)}
                                    title="Löschen"
                                  >
                                    <i className="bi bi-trash-fill"></i>
                                  </button>
                                </div>
                                <div className="dropdown d-md-none">
                                  <button 
                                    className="btn btn-outline-secondary btn-sm dropdown-toggle" 
                                    type="button" 
                                    data-bs-toggle="dropdown"
                                    title="Aktionen"
                                  >
                                    <i className="bi bi-three-dots-vertical"></i>
                                  </button>
                                  <ul className="dropdown-menu">
                                    <li>
                                      <button
                                        className="dropdown-item"
                                        onClick={() => handleViewDocument(doc.id, doc.user_id)}
                                      >
                                        <i className="bi bi-eye-fill me-2"></i>Öffnen
                                      </button>
                                    </li>
                                    <li>
                                      <button
                                        className="dropdown-item text-danger"
                                        onClick={() => deleteDocument(doc.id)}
                                      >
                                        <i className="bi bi-trash-fill me-2"></i>Löschen
                                      </button>
                                    </li>
                                  </ul>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );

                  return (
                    <>
                      {/* Current Year Documents */}
                      <div className="card">
                        <div className="card-header">
                          <h6 className="mb-0">
                            <i className="bi bi-file-earmark-text me-2"></i>
                            Dokumente {currentYear}
                          </h6>
                        </div>
                        <div className="card-body">
                          {currentYearDocs.length === 0 ? (
                            <p className="text-muted">Keine Dokumente für {currentYear} vorhanden</p>
                          ) : renderDocTable(currentYearDocs)}
                        </div>
                      </div>

                      {/* Archive Documents */}
                      {archiveYears.length > 0 && (
                        <div className="card mt-3">
                          <div className="card-header bg-light">
                            <h6 className="mb-0 text-muted">
                              <i className="bi bi-archive me-2"></i>
                              Archiv
                            </h6>
                          </div>
                          <div className="card-body">
                            {archiveYears.map(year => (
                              <div key={year} className="mb-3">
                                <h6 className="text-muted border-bottom pb-1">
                                  <span className="badge bg-secondary me-2">{year}</span>
                                  {archiveByYear[year].length} Dokument{archiveByYear[year].length !== 1 ? 'e' : ''}
                                </h6>
                                {renderDocTable(archiveByYear[year])}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Partner Preferences Modal */}
      {showPartnerModal && selectedUser && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Arbeitspartner-Wünsche - {selectedUser.firstname && selectedUser.lastname 
                    ? `${selectedUser.firstname} ${selectedUser.lastname}` 
                    : selectedUser.name || 'Benutzer'}
                </h5>
                <button className="btn-close" onClick={() => {
                  setShowPartnerModal(false);
                  setPartnerData({ preferred_partner_id: '', priority: 1, notes: '', bidirectional: true });
                }}></button>
              </div>
              <div className="modal-body">
                {/* Add Partner Preference Form */}
                <div className="card mb-3">
                  <div className="card-header">
                    <h6 className="mb-0">Arbeitspartner-Wunsch hinzufügen</h6>
                  </div>
                  <div className="card-body">
                    <form onSubmit={handlePartnerSubmit}>
                      <div className="row">
                        <div className="col-md-6">
                          <label className="form-label">Gewünschter Partner</label>
                          <select
                            className="form-select"
                            value={partnerData.preferred_partner_id}
                            onChange={e => setPartnerData({...partnerData, preferred_partner_id: parseInt(e.target.value)})}
                            required
                          >
                            <option value="">Partner auswählen...</option>
                            {users
                              .filter(u => u.id !== selectedUser.id) // Exclude self
                              .map(user => (
                                <option key={user.id} value={user.id}>
                                  {user.firstname && user.lastname 
                                    ? `${user.firstname} ${user.lastname}` 
                                    : user.name || 'Unbekannt'}
                                </option>
                              ))}
                          </select>
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Priorität</label>
                          <select
                            className="form-select"
                            value={partnerData.priority}
                            onChange={e => setPartnerData({...partnerData, priority: parseInt(e.target.value)})}
                          >
                            <option value={1}>Hoch (1)</option>
                            <option value={2}>Mittel (2)</option>
                            <option value={3}>Niedrig (3)</option>
                          </select>
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">&nbsp;</label>
                          <button type="submit" className="btn btn-primary d-block">
                            Hinzufügen
                          </button>
                        </div>
                      </div>
                      <div className="mt-2">
                        <label className="form-label">Notizen</label>
                        <input
                          type="text"
                          className="form-control"
                          value={partnerData.notes}
                          onChange={e => setPartnerData({...partnerData, notes: e.target.value})}
                          placeholder="z.B. Arbeitet gerne zusammen, gute Kommunikation"
                        />
                      </div>
                      <div className="mt-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="bidirectionalPartner"
                            checked={partnerData.bidirectional !== false}
                            onChange={e => setPartnerData({...partnerData, bidirectional: e.target.checked})}
                          />
                          <label className="form-check-label" htmlFor="bidirectionalPartner">
                            <strong>Bidirektionale Beziehung</strong>
                            <small className="text-muted d-block">
                              Fügt auch bei dem gewählten Partner automatisch eine Rückverbindung hinzu
                            </small>
                          </label>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>

                {/* Partner Preferences List */}
                <div className="card">
                  <div className="card-header">
                    <h6 className="mb-0">Aktuelle Arbeitspartner-Wünsche</h6>
                  </div>
                  <div className="card-body">
                    {partnerPreferences.length === 0 ? (
                      <p className="text-muted">Keine Arbeitspartner-Wünsche definiert</p>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>Partner</th>
                              <th>Priorität</th>
                              <th>Notizen</th>
                              <th>Aktionen</th>
                            </tr>
                          </thead>
                          <tbody>
                            {partnerPreferences.map(pref => {
                              return (
                                <tr key={pref.id}>
                                  <td><strong>
                                    {pref.firstname && pref.lastname 
                                      ? `${pref.firstname} ${pref.lastname}` 
                                      : 'Unbekannt'}
                                  </strong>
                                  {pref.direction === 'incoming' && <span className="badge bg-info ms-2" style={{fontSize: '0.65rem'}}>↩ bidirektional</span>}
                                  </td>
                                  <td>
                                    <span className={`badge bg-${pref.priority === 1 ? 'success' : pref.priority === 2 ? 'warning' : 'secondary'}`}>
                                      {pref.priority === 1 ? 'Hoch' : pref.priority === 2 ? 'Mittel' : 'Niedrig'} ({pref.priority})
                                    </span>
                                  </td>
                                  <td>{pref.notes || '-'}</td>
                                  <td>
                                    <button
                                      className="btn btn-outline-danger btn-sm"
                                      onClick={() => handleDeletePartner(pref.direction === 'incoming' ? pref.user_id : pref.preferred_partner_id)}
                                      title="Löschen"
                                    >
                                      <i className="bi bi-trash-fill"></i>
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Work Years Modal */}
      {showWorkYearModal && selectedUser && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Arbeitsjahre - {selectedUser.firstname && selectedUser.lastname 
                    ? `${selectedUser.firstname} ${selectedUser.lastname}` 
                    : selectedUser.name || 'Benutzer'}
                </h5>
                <button className="btn-close" onClick={() => setShowWorkYearModal(false)}></button>
              </div>
              <div className="modal-body">
                {/* Add Work Year Form */}
                <div className="card mb-3">
                  <div className="card-header">
                    <h6 className="mb-0">Arbeitsjahr hinzufügen</h6>
                  </div>
                  <div className="card-body">
                    <form onSubmit={handleWorkYearSubmit}>
                      <div className="row">
                        <div className="col-md-4">
                          <label className="form-label">Jahr</label>
                          <input
                            type="number"
                            className="form-control"
                            min="2020"
                            max="2030"
                            value={workYearData.year}
                            onChange={e => setWorkYearData({...workYearData, year: parseInt(e.target.value)})}
                            required
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Status</label>
                          <select
                            className="form-select"
                            value={workYearData.is_active}
                            onChange={e => setWorkYearData({...workYearData, is_active: e.target.value === 'true'})}
                          >
                            <option value={true}>Aktiv</option>
                            <option value={false}>Inaktiv</option>
                          </select>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">&nbsp;</label>
                          <button type="submit" className="btn btn-primary d-block">
                            Hinzufügen
                          </button>
                        </div>
                      </div>
                      <div className="mt-2">
                        <label className="form-label">Notizen</label>
                        <input
                          type="text"
                          className="form-control"
                          value={workYearData.notes}
                          onChange={e => setWorkYearData({...workYearData, notes: e.target.value})}
                          placeholder="z.B. Grund für Inaktivität"
                        />
                      </div>
                    </form>
                  </div>
                </div>

                {/* Work Years List */}
                <div className="card">
                  <div className="card-header">
                    <h6 className="mb-0">Arbeitsjahre</h6>
                  </div>
                  <div className="card-body">
                    {userWorkYears.length === 0 ? (
                      <p className="text-muted">Keine Arbeitsjahre definiert</p>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>Jahr</th>
                              <th>Status</th>
                              <th>Notizen</th>
                              <th>Aktionen</th>
                            </tr>
                          </thead>
                          <tbody>
                            {userWorkYears.map(wy => (
                              <tr key={wy.id}>
                                <td><strong>{wy.year}</strong></td>
                                <td>
                                  <button
                                    className={`btn btn-sm ${wy.is_active ? 'btn-success' : 'btn-secondary'}`}
                                    onClick={() => toggleWorkYearActive(wy, wy.is_active)}
                                    title={wy.is_active ? 'Auf inaktiv setzen' : 'Auf aktiv setzen'}
                                  >
                                    {wy.is_active ? 'Aktiv' : 'Inaktiv'}
                                  </button>
                                </td>
                                <td>{wy.notes || '-'}</td>
                                <td>
                                  <button
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => deleteWorkYear(wy)}
                                    title="Löschen"
                                  >
                                    <i className="bi bi-trash-fill"></i>
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* WhatsApp Chat Modal */}
      {showWhatsAppChat && selectedChatUser && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <WhatsAppChat
                userId={selectedChatUser.id}
                userName={`${selectedChatUser.firstname} ${selectedChatUser.lastname}`}
                onClose={closeWhatsAppChat}
              />
            </div>
          </div>
        </div>
      )}

      {/* Contract Document Viewer Modal */}
      {showContractDocModal && contractDocImage && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {contractDocTitle}
                  {contractDocImage.startsWith('data:image') && (
                    <small className="text-muted fw-normal ms-3">🔍 {cdZoom ? 'Klick = zurück' : 'Klick ins Bild = zoomen'}</small>
                  )}
                </h5>
                <button className="btn-close" onClick={() => { setCdZoom(null); setShowContractDocModal(false); }}></button>
              </div>
              <div className="modal-body text-center">
                {contractDocImage.startsWith('data:image') ? (
                  // Klick zoomt auf 2,5× an der geklickten Stelle (Ausweisnummer!), gezoomt scrollbar
                  <div ref={cdScrollRef}
                    style={{ maxHeight: '80vh', overflow: cdZoom ? 'auto' : 'hidden',
                      display: 'flex', justifyContent: cdZoom ? 'flex-start' : 'center' }}>
                    <img src={contractDocImage} alt={contractDocTitle}
                      onClick={(e) => {
                        if (cdZoom) { setCdZoom(null); return; }
                        const r = e.currentTarget.getBoundingClientRect();
                        setCdZoom({ x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height });
                      }}
                      style={cdZoom
                        ? { width: '250%', maxWidth: 'none', height: 'auto', cursor: 'zoom-out', flexShrink: 0 }
                        : { maxWidth: '100%', maxHeight: '80vh', cursor: 'zoom-in' }} />
                  </div>
                ) : contractDocImage.startsWith('data:application/pdf') ? (
                  <iframe
                    src={contractDocImage}
                    title={contractDocTitle}
                    style={{ width: '100%', height: '80vh', border: 'none' }}
                  />
                ) : (
                  <p>Dokumentformat wird nicht unterstützt.</p>
                )}
              </div>
              <div className="modal-footer">
                <a
                  href={contractDocImage}
                  download={contractDocTitle.replace(/\s+/g, '_') + (contractDocImage.startsWith('data:image') ? '.png' : '.pdf')}
                  className="btn btn-outline-primary"
                >
                  <i className="bi bi-download me-1"></i>Herunterladen
                </a>
                <button className="btn btn-secondary" onClick={() => { setCdZoom(null); setShowContractDocModal(false); }}>Schließen</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
